import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { PageHero } from "@/components/Shared";
import { StaggerGroup, StaggerItem } from "@/components/Reveal";
import { GALLERY, IMAGES } from "@/data/site";

const spanClass = {
  tall: "md:row-span-2",
  wide: "md:col-span-2",
  normal: "",
};

export default function Gallery() {
  const [active, setActive] = useState(null);

  return (
    <div data-testid="gallery-page">
      <PageHero
        eyebrow="THE GALLERY"
        title="Shot At Speed"
        subtitle="Drone photography, motion blur and wheel-to-wheel action from the Karting Bahamas circuit."
        image={IMAGES.trackDrone2}
        testid="gallery-hero"
      />

      <section className="bg-[#050505] py-16 md:py-24">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <StaggerGroup className="grid auto-rows-[220px] grid-cols-2 gap-4 md:grid-cols-4">
            {GALLERY.map((g, i) => (
              <StaggerItem key={i} className={spanClass[g.span] || ""}>
                <button
                  onClick={() => setActive(g)}
                  data-testid={`gallery-item-${i}`}
                  className="group relative h-full w-full overflow-hidden rounded-xl"
                >
                  <img src={g.src} alt={g.label} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-70 transition-opacity group-hover:opacity-90" />
                  <div className="absolute bottom-4 left-4 translate-y-2 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                    <span className="font-display text-2xl tracking-wide text-white">{g.label}</span>
                  </div>
                  <div className="absolute right-3 top-3 h-2 w-2 rounded-full bg-kbyellow opacity-0 transition-opacity group-hover:opacity-100" />
                </button>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
            className="fixed inset-0 z-[90] flex items-center justify-center bg-black/90 p-4 backdrop-blur-md"
          >
            <button
              data-testid="gallery-lightbox-close"
              className="absolute right-6 top-6 flex h-12 w-12 items-center justify-center rounded-xl border border-white/20 text-white hover:border-kbyellow hover:text-kbyellow"
              onClick={() => setActive(null)}
            >
              <X className="h-6 w-6" />
            </button>
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-h-[85vh] max-w-5xl overflow-hidden rounded-xl"
            >
              <img src={active.src} alt={active.label} className="max-h-[85vh] w-full object-contain" />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                <span className="font-display text-3xl tracking-wide text-kbcyan">{active.label}</span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
